import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { HelperService } from 'src/app/public/helper.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StoresActionType } from 'src/app/stores/+state/stores.actions';
import { Actions, ofType } from '@ngrx/effects';

/**
 * What the tile holds. `video` is the kiosk inactivity video and the default;
 * `pdf` is the Marketing brochure (story #11626). The two differ in validation,
 * in the rejection toast, in whether a thumbnail can be rendered, and in whether
 * the caption survives once a file exists — everything else is shared.
 */
export type UploadFileKind = 'video' | 'pdf';

const BYTES_PER_MB = 1024 * 1024;

@Component({
  selector: 'app-upload-video',
  templateUrl: './upload-video.component.html',
  styleUrl: './upload-video.component.scss',
})
export class UploadVideoComponent implements OnInit, OnChanges, OnDestroy {
  @Input() currentVideoUrl: string;
  @Input() label: string;
  @Input() infoMessage: string;
  @Input() acceptedFormats: string = '.mp4, .webm, .mov';
  @Input() submitVideo: (file: File) => void;
  @Input() removeVideo: () => void;

  /** Drives validation and display. Defaults to the kiosk video behaviour. */
  @Input() fileKind: UploadFileKind = 'video';
  /** Caption shown while the tile is empty. */
  @Input() uploadText: string = 'admin.store.events.uploadReel';
  /**
   * Caption shown once a file exists. Left unset for the video, which shows its
   * thumbnail instead of a caption — the PDF has no thumbnail, so it needs one.
   */
  @Input() replaceText: string;
  /**
   * Client-side size guard, in MB. Unset means only the backend 413 applies.
   * The rejection reuses the fixed "…50 MB" message, so a caller sets this to
   * `PLATFORM_MAX_UPLOAD_SIZE_MB`.
   */
  @Input() maxSizeMb: number;
  /** Renders a download icon beside the tile once a file exists. */
  @Input() showDownload: boolean = false;
  /** File name suggested to the browser by the download icon. */
  @Input() downloadFileName: string = 'download';

  reelImage: Object;
  video: File;
  isUploading: boolean = false;
  private cachedImageUrl: { [key: string]: any } = {};
  private destroy$ = new Subject<void>();

  constructor(
    private translateService: TranslateService,
    private toastr: ToastrService,
    private helperService: HelperService,
    private sanitizer: DomSanitizer,
    private store: Store,
    private actions$: Actions,
  ) {}

  ngOnInit(): void {
    this.generateThumbnail(this.currentVideoUrl);
    this.setupUploadListeners();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupUploadListeners(): void {
    // Listen for upload success
    this.actions$
      .pipe(
        ofType(StoresActionType.UploadStoreVideoSuccess),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isUploading = false;
      });

    // Listen for upload failure
    this.actions$
      .pipe(
        ofType(StoresActionType.UploadStoreVideoFailed),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isUploading = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.generateThumbnail(this.currentVideoUrl);
  }

  generateThumbnail(videoUrl: string) {
    this.reelImage = null;
    // A PDF has no frame to grab, and the helper would reject the URL anyway.
    if (this.fileKind === 'pdf') {
      return;
    }
    if (videoUrl) {
      this.helperService.generateThumbnail(videoUrl).then((res) => {
        this.reelImage = this.getImage(res);
      });
    }
  }

  getImage(image: string) {
    if (!this.cachedImageUrl[image]) {
      this.cachedImageUrl[image] = this.sanitizer.bypassSecurityTrustStyle(
        `url('${image}')`,
      );
    }
    return this.cachedImageUrl[image];
  }

  /**
   * A thumbnail is only ever available for a video. Everything else the tile
   * shows is derived from this, so the PDF path keeps the cloud icon where the
   * video shows its frame.
   */
  get showThumbnail(): boolean {
    return this.fileKind === 'video' && !!this.reelImage;
  }

  /**
   * Whether a file is already stored. The video treats its thumbnail as the
   * sign of a file, so its ✕ appears with the thumbnail; the PDF reads the URL
   * directly.
   */
  get hasFile(): boolean {
    return this.fileKind === 'pdf' ? !!this.currentVideoUrl : !!this.reelImage;
  }

  /** An empty tile shows the upload caption; a filled one shows `replaceText`. */
  get captionKey(): string {
    return this.hasFile && this.replaceText
      ? this.replaceText
      : this.uploadText;
  }

  /**
   * With no `replaceText` the caption disappears once a file exists, because
   * the video's thumbnail takes its place.
   */
  get showCaption(): boolean {
    return !this.hasFile || !!this.replaceText;
  }

  selectVideo(event) {
    event.target.value = null;
  }

  uploadVideo(fileEvent: Event) {
    const fileInput = fileEvent.target as HTMLInputElement;

    if (fileInput?.files?.length) {
      this.video = fileInput.files[0];
      if (!this.isAcceptedFile(this.video)) {
        this.showRejection(this.invalidFormatMessageKey());
        this.video = null;
        return;
      }

      if (this.exceedsMaxSize(this.video)) {
        this.showRejection('admin.store.settings.fileSizeError');
        this.video = null;
        return;
      }

      this.isUploading = true;
      this.submitVideo(this.video);
    }
  }

  /**
   * Some systems hand back an empty MIME type for a PDF, so the extension is
   * accepted as a fallback. The backend has the final say: it keeps the file
   * only if its first bytes are `%PDF-`, whatever type the browser sent.
   */
  private isAcceptedFile(file: File): boolean {
    if (this.fileKind === 'pdf') {
      return (
        file.type === 'application/pdf' ||
        !!file.name?.toLowerCase().endsWith('.pdf')
      );
    }
    return file.type.startsWith('video/');
  }

  private invalidFormatMessageKey(): string {
    return this.fileKind === 'pdf'
      ? 'admin.store.events.invalidFileFormat'
      : 'admin.store.events.invalidVideoFormat';
  }

  private exceedsMaxSize(file: File): boolean {
    return !!this.maxSizeMb && file.size > this.maxSizeMb * BYTES_PER_MB;
  }

  /**
   * Toast for a file the tile refuses to send. The video keeps its own toast:
   * the message plus a full stop, under the English title "Upload failed!".
   * The PDF shows the message as written, under the translated title.
   */
  private showRejection(messageKey: string): void {
    const message = this.translateService.instant(messageKey);
    if (this.fileKind === 'pdf') {
      this.toastr.error(
        message,
        this.translateService.instant('admin.store.message.uploadFail'),
      );
      return;
    }
    this.toastr.error(`${message}.`, 'Upload failed!');
  }

  /**
   * Same approach as `downloadImage()` in `promotion-form.component.ts`: the
   * stored file is served with `Content-Disposition: attachment`, so a plain
   * link click is all a download needs.
   *
   * `downloadFileName` is a best-effort hint only. On the web admin the file
   * is served from the admin's own origin (`<admin>/api/v1/files/...`), where
   * the `download` attribute applies. In the apps the page runs on
   * `localhost`, so the URL is cross-origin and browsers ignore `download`;
   * the saved name then comes from the backend's `Content-Disposition`
   * (`store-brochure.pdf` for the brochure).
   *
   * The anchor is appended before clicking: a detached one works in current
   * Chrome, Safari and Firefox, but not in every older WebView.
   */
  downloadFile() {
    if (!this.isDownloadableUrl(this.currentVideoUrl)) {
      return;
    }
    const link = document.createElement('a');
    link.href = this.currentVideoUrl;
    link.download = this.downloadFileName;
    document.body.appendChild(link);
    try {
      link.click();
    } finally {
      document.body.removeChild(link);
    }
  }

  /**
   * The backend only lets an upload set the brochure URL, but this link runs
   * in the admin origin, so it does not rely on that: clicking a `javascript:`
   * link runs it even with `download` set, so only http(s) is followed.
   * Parsing, rather than a prefix check, also catches the case, whitespace and
   * control-character variants a browser still reads as `javascript:`.
   */
  private isDownloadableUrl(url: string): boolean {
    if (!url) {
      return false;
    }
    try {
      const { protocol } = new URL(url, window.location.href);
      return protocol === 'http:' || protocol === 'https:';
    } catch {
      // Not a URL at all, e.g. `https://` with no host.
      return false;
    }
  }

  processRemoveVideo() {
    this.removeVideo();
  }
}
